import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Scale, Zap, Route, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutPage = () => {
  const scrollToIntake = () => {
    window.location.assign('/#intake');
  };

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Back Link */}
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        {/* Hero Section */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10 mb-12">
          <div className="max-w-4xl">
            <div className="inline-block px-4 py-1 rounded-full text-sm font-medium mb-6 bg-[#d9fb06]/20 text-[#d9fb06]">
              Our Story
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#d9fb06] mb-6 tracking-tight">
              About Us
            </h1>
            <p className="text-xl md:text-2xl text-[#dfddd6] leading-relaxed">
              Two professionals. One shared problem. <span className="text-[#d9fb06]">One clear solution.</span>
            </p>
          </div>
        </section>

        {/* Main Content */}
        <section className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="max-w-4xl">
            
            {/* Intro */}
            <div className="mb-12">
              <p className="text-lg text-[#dfddd6] leading-relaxed mb-6">
                Social Dispute Co is an independent consulting service focused on helping individuals 
                organize documentation when submitting appeals through official online platform processes.
              </p>
              <p className="text-lg text-[#dfddd6] leading-relaxed mb-6">
                Our role is limited to documentation preparation and case organization. All appeals must 
                be submitted directly by the account holder through official platform channels.
              </p>
              <p className="text-lg text-[#dfddd6] leading-relaxed">
                We do not access accounts, contact platforms on behalf of clients, or bypass platform 
                safeguards. Outcomes depend entirely on the platform's review process and policies.
              </p>
            </div>

            {/* About Our Approach */}
            <div className="bg-[#302f2c] rounded-2xl p-8 mb-12 border border-[#3f4816]/50">
              <h3 className="text-[#d9fb06] font-bold text-lg mb-4">Our Documentation Approach</h3>
              
              <div className="space-y-4 text-[#dfddd6]">
                <p>
                  We help clients organize their case materials—timelines, evidence, and policy-relevant 
                  context—into structured documentation suitable for official appeal submissions.
                </p>
                <p>
                  Clients submit their appeals directly through the platform's official appeal systems. 
                  We provide documentation support only.
                </p>
                <p className="text-[#888680]">
                  Social Dispute Co is not affiliated with Meta, TikTok, YouTube, X, or any social media 
                  platform. We do not guarantee outcomes.
                </p>
              </div>
            </div>

            {/* What We Do */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#d9fb06] mb-6">
                What We Help With
              </h2>
              <p className="text-lg text-[#dfddd6] mb-6">
                We help clients organize and prepare documentation for appeal submissions. This includes:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3 text-[#dfddd6]">
                  <span className="text-[#d9fb06] mt-1">•</span>
                  Organizing timelines and account history
                </li>
                <li className="flex items-start gap-3 text-[#dfddd6]">
                  <span className="text-[#d9fb06] mt-1">•</span>
                  Compiling relevant evidence and documentation
                </li>
                <li className="flex items-start gap-3 text-[#dfddd6]">
                  <span className="text-[#d9fb06] mt-1">•</span>
                  Providing context on platform policies
                </li>
              </ul>
              <p className="text-[#888680]">
                Clients submit their appeals directly through official platform channels.
              </p>
            </div>

            {/* What We Offer */}
            <div className="mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#d9fb06] mb-8">
                Our Services
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-[#302f2c] rounded-xl p-6 border border-[#3f4816]/50">
                  <Scale className="w-10 h-10 text-[#d9fb06] mb-4" />
                  <h3 className="text-[#dfddd6] font-bold text-lg mb-2">Case Organization</h3>
                  <p className="text-[#888680] text-sm">Structured documentation preparation</p>
                </div>
                <div className="bg-[#302f2c] rounded-xl p-6 border border-[#3f4816]/50">
                  <Route className="w-10 h-10 text-[#d9fb06] mb-4" />
                  <h3 className="text-[#dfddd6] font-bold text-lg mb-2">Submission Guidance</h3>
                  <p className="text-[#888680] text-sm">Information about official appeal channels</p>
                </div>
                <div className="bg-[#302f2c] rounded-xl p-6 border border-[#3f4816]/50">
                  <Zap className="w-10 h-10 text-[#d9fb06] mb-4" />
                  <h3 className="text-[#dfddd6] font-bold text-lg mb-2">Process Support</h3>
                  <p className="text-[#888680] text-sm">Guidance throughout the documentation process</p>
                </div>
              </div>
            </div>

            {/* Location & Trust */}
            <div className="bg-[#302f2c] rounded-2xl p-8 mb-12 border border-[#3f4816]/50">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-6 h-6 text-[#d9fb06]" />
                <h3 className="text-xl font-bold text-[#d9fb06]">Honolulu, Hawaii</h3>
              </div>
              <p className="text-[#dfddd6] leading-relaxed">
                We are a U.S.-based company. Transparency and credibility matter to us. That's why we offer a complimentary evaluation call: to review your situation, explain what is realistically possible, and introduce you to the professionals behind our work.
              </p>
            </div>

            {/* CTA */}
            <div className="text-center mb-12">
              <h3 className="text-xl font-bold text-[#dfddd6] mb-4">
                Request Free Evaluation by filling out our intake form:
              </h3>
              <Button
                onClick={scrollToIntake}
                className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90 rounded-full px-8 py-6 text-lg font-bold"
              >
                Get Free Evaluation
              </Button>
            </div>

            {/* Quote */}
            <div className="border-l-4 border-[#d9fb06] pl-6 py-2">
              <p className="text-lg text-[#dfddd6] italic">
                "What took months of trial, error, and substantial expense to solve the first time, we now help others navigate far more efficiently—so you can focus on what matters most."
              </p>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
